'use client'

// Pestañas «Restaurante | Mercado» de la portada de un local con platos y súper.
// Se pegan debajo del header y de la barra de entrega; publican su alto en `--ms-tabs-h` para
// que la barra de categorías (STOREFRONT_TABS_STICKY_TOP) se pegue debajo de ellas.
import { useEffect, useRef } from 'react'
import { useTranslations } from 'next-intl'
import type { StorefrontSection } from '@/lib/microsite/storefrontKind'

const TABS: { id: StorefrontSection; emoji: string; labelKey: 'tabRestaurant' | 'tabMarket' }[] = [
  { id: 'restaurant', emoji: '🍜', labelKey: 'tabRestaurant' },
  { id: 'market', emoji: '🛒', labelKey: 'tabMarket' },
]

export function StorefrontTabs({
  active,
  onChange,
  showWeightLine,
}: {
  active: StorefrontSection
  onChange: (s: StorefrontSection) => void
  showWeightLine: boolean
}) {
  const t = useTranslations('public.microsite.grocery.sections')
  const barRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const bar = barRef.current
    if (!bar) return
    const root = document.documentElement
    const publish = () => root.style.setProperty('--ms-tabs-h', `${Math.round(bar.offsetHeight)}px`)
    publish()
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(publish) : null
    ro?.observe(bar)
    return () => {
      ro?.disconnect()
      root.style.removeProperty('--ms-tabs-h')
    }
  }, [])

  return (
    <div
      ref={barRef}
      data-testid="storefront-tabs-bar"
      className="sticky z-20"
      style={{
        // Va DEBAJO de nav + barra de entrega; su propio alto no entra (eso es --ms-tabs-h).
        top: 'calc(var(--ms-nav-h, 56px) + var(--ms-fulfill-h, 0px))',
        background: 'var(--ms-bg)',
      }}
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-3">
        <div role="tablist" aria-label={t('tablistAria')} className="flex gap-2">
          {TABS.map((tab) => {
            const isActive = tab.id === active
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => onChange(tab.id)}
                className="flex-1 rounded-full px-3 py-2 text-[14px] font-semibold transition border"
                style={
                  isActive
                    ? { background: 'var(--ms-text)', color: 'var(--ms-bg)', borderColor: 'var(--ms-text)' }
                    : { background: 'transparent', color: 'var(--ms-text-muted)', borderColor: 'var(--ms-border)' }
                }
              >
                <span aria-hidden="true">{tab.emoji}</span> {t(tab.labelKey)}
              </button>
            )
          })}
        </div>
        <p data-testid="storefront-tab-line" className="text-[13px] py-2" style={{ color: 'var(--ms-text-muted)' }}>
          {active === 'restaurant'
            ? t('restaurantLine')
            : showWeightLine
              ? `${t('marketLine')} ${t('marketWeightLine')}`
              : t('marketLine')}
        </p>
      </div>
    </div>
  )
}
